/**
 * useMediaQuery (supports items #15, #20 Phase 1/2).
 *
 * Returns boolean for whether a media query matches right now. Subscribes
 * to changes. SSR-safe (returns false on server).
 */
import { useEffect, useState } from 'react';

function getMediaQueryList(query: string) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(query);
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    return getMediaQueryList(query)?.matches ?? false;
  });

  useEffect(() => {
    const mq = getMediaQueryList(query);
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mq.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, [query]);

  return matches;
}

/** Convenience: < 1024px (list pane should collapse to icons). */
export function useIsNarrowViewport(): boolean {
  return useMediaQuery('(max-width: 1023px)');
}

/** Convenience: < 768px (nav rail should hide behind hamburger). */
export function useIsMobileViewport(): boolean {
  return useMediaQuery('(max-width: 767px)');
}
